import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CollectionResponse, CollectionRequest, CollectionsService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-edit-collection',
    templateUrl: './edit-collection.component.html',
    styleUrl: './edit-collection.component.scss',
    host: {
        class: 'app-edit-collection'
    },
    standalone: false
})
export class EditCollectionComponent extends BaseViewComponent implements OnInit {

  collectionId: number = 0;
  collection: CollectionResponse = {};

  filterOperator = FilterOperator;
  collectionsList: any[] = [];
  collectionsTotalRecords: number = 0;
  collectionsRowsPerPageOption: number = 10;

  public collectionForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    code: ['', [Validators.required, Validators.maxLength(50)]],
    hasParentCollection: ['false'],
    collection: [[]]
  },
    { validators: this.conditionalValidators() });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private collectionsService: CollectionsService,
    private fb: FormBuilder
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.collectionId = params['collectionId'];
      await this.getCollections({ pageIndex: 1, pageSize: this.collectionsRowsPerPageOption, search: '' });

      if (this.collectionId) {
        this.collection = await firstValueFrom(this.collectionsService.apiCollectionsIdGet(this.collectionId));
        await this.getCollections({ pageIndex: 1, pageSize: this.collectionsRowsPerPageOption, search: '' });
        this.ensureSelectedParentCollection();
        this.initForm();
      }
    });
  }

  isCollectionRequired(): boolean {
    return this.collectionForm.get('hasParentCollection')?.value === 'true';
  }

  conditionalValidators(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const hasParentCollection = formGroup.get('hasParentCollection')?.value;
      const collection = formGroup.get('collection')?.value ?? [];

      if (hasParentCollection === 'true' && collection.length === 0) {
        return { requiredCollection: true };
      }

      return null;
    };
  }

  async onLazyLoadCollections(event: any) {
    await this.getCollections(event);
  }

  async getCollections(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload: FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.collectionsRowsPerPageOption
    }
    if (search) {
      payload.textFilter = search;
    }

    const collectionsPage = await firstValueFrom(this.collectionsService.apiCollectionsFilterComboPost(payload));

    // prevent adding itself as parent
    this.collectionsList = (collectionsPage.results ?? []).filter(c => c.id != this.collectionId);

    this.collectionsTotalRecords = collectionsPage.totalCount ?? 0;

    if (this.collection?.parent?.id && this.collection.parent.id != this.collectionId) {
      const exists = this.collectionsList.some((c: any) => c.id == this.collection.parent?.id);
      if (!exists) {
        const current = await firstValueFrom(this.collectionsService.apiCollectionsComboValueIdGet(this.collection.parent.id))
        this.collectionsList = [current, ...this.collectionsList];
      }
    }
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  initForm() {
    this.collectionForm.controls['name'].setValue(this.collection.name);
    this.collectionForm.controls['code'].setValue(this.collection.code);

    if (this.collection.parentId) {
      this.collectionForm.controls['hasParentCollection'].setValue('true');
      this.collectionForm.controls['collection'].setValue([this.collection.parentId]);
    }

    this.collectionForm.get('hasParentCollection')?.valueChanges.subscribe((hasParent) => {
      if (hasParent === 'false') {
        this.collectionForm.controls['collection'].setValue([]);
      }
    });
  }

  private ensureSelectedParentCollection(): void {
    const parentId = this.collection.parentId;
    if (!parentId) return;

    // if parentCollection is not in the 1st loaded page
    const exists = this.collectionsList.some((c: any) => c.id === parentId);
    if (!exists) {
      this.collectionsList = [
        {
          id: parentId,
          name: (this.collection as any).parent?.name ?? `#${parentId}`
        },
        ...this.collectionsList
      ];
    }
  }

  async submitForm() {
    if (this.collectionForm.valid) {

      const payload: CollectionRequest = {
        name: this.collectionForm.controls['name'].value,
        code: this.collectionForm.controls['code'].value,
        parentId: this.collectionForm.controls['collection']?.value?.[0]
      };

      await firstValueFrom(this.collectionsService.apiCollectionsUpdateIdPut(this.collectionId, payload));

      this.router.navigate(['/collections', this.collectionId]);
    }
  }
}
